// =============================================================================
// Dashboard Orders -- Address Picker, Payment, Order Modal, Order List
// Extracted from make_album/dashboard.html inline JS
// =============================================================================

declare const QRCode: any;
declare function trackEvent(event: string, data?: Record<string, unknown>, opts?: Record<string, unknown>): void;

// Pricing fetched from server (shared by all order modals)
var _pdfPrice = 0;
var _creditsPerYuan = 100;
var _pricingLoaded = false;

async function _fetchPricing(): Promise<void> {
  if (_pricingLoaded) return;
  try {
    var resp = await fetch('/api/pricing');
    var data = await resp.json();
    _pdfPrice = data.pdf_price;
    _creditsPerYuan = data.credits_per_yuan;
    _pricingLoaded = true;
  } catch(e) {
    // Use defaults on failure
    console.warn('Failed to load pricing, using defaults:', e);
  }
}

export function getPdfPrice(): number {
  return _pdfPrice;
}

export function getCreditsPerYuan(): number {
  return _creditsPerYuan;
}

import type {
  Address,
  Album,
  AlbumDetail,
  ChinaRegions,
  Province,
  City,
  District,
  Order,
  OrderCreateResponse,
  OrderPreviewPage,
  PageData,
  PaymentInfo,
  PaymentResult,
  PaymentIntentStatus,
  AddressSnapshot,
  OrderState,
  Spread,
  StatusMap,
} from './dashboard-types';

import {
  loadAlbumFromDB,
  loadUserAlbums,
  loadTemplateRegistry,
  getTemplateMeta,
  getPageRenderSize,
  loadTemplateRenderer,
  loadTemplateCSS,
  invalidateAlbumsCache,
  loadChinaRegions,
  renderSavedPageIntoHost,
  loadAddresses,
  saveAddressesToCache,
  createAddressOnServer,
  updateAddressOnServer,
} from './dashboard-data';

import {
  showToast,
  showPrompt,
  showConfirm,
  showWarning,
  escapeHtml,
  formatTime,
} from './dashboard-ui';

import { currentView, setCurrentView, renderView } from './dashboard-auth';

// =============================================================================
// Constants
// =============================================================================

// Physical album type configuration
interface PhysicalAlbumType {
  id: string;                    // 'hardcover-hard' | 'hardcover-soft'
  name: string;                  // '硬壳硬页' | '硬壳软页'
  description: string;           // 描述文字
  basePrice: number;             // 基础价格（119元）
  basePages: number;             // 基础页数
  extraPricePerPage: number;     // 超出页面单价
  extraPagesUnit: number;        // 超出页数计算单位（1或2）
  maxPages: number;              // 最大页数
  features: string[];            // 特性标签
}

const PHYSICAL_ALBUM_TYPES: PhysicalAlbumType[] = [
  {
    id: 'hardcover-hard',
    name: '硬壳硬页',
    description: '硬壳硬面 · 艺术微喷 · 哑光质感',
    basePrice: 119,
    basePages: 24,
    extraPricePerPage: 8,
    extraPagesUnit: 2,
    maxPages: 60,
    features: ['21cm × 21cm', '硬壳硬面', '艺术微喷', '哑光质感']
  },
  {
    id: 'hardcover-soft',
    name: '硬壳软页',
    description: '硬壳软面 · 米色190克美华艺术纸',
    basePrice: 119,
    basePages: 34,
    extraPricePerPage: 3,
    extraPagesUnit: 1,
    maxPages: 120,
    features: ['21cm × 21cm', '硬壳软面', '米色190克美华艺术纸']
  }
];

function calculatePhysicalPrice(type: PhysicalAlbumType, internalPages: number): number {
  if (internalPages <= type.basePages) {
    return type.basePrice;
  }
  const extraPages = internalPages - type.basePages;
  const extraUnits = Math.ceil(extraPages / type.extraPagesUnit);
  return type.basePrice + extraUnits * type.extraPricePerPage;
}

const EXCLUDED_PROVINCES: string[] = ['香港特别行政区', '澳门特别行政区', '台湾省'];

const CUSTOMER_SERVICE_QR: string = 'assets/customer-service-wecom.png';

const STATUS_MAP: StatusMap = {
  paid: '已付款',
  production_file: '生产印刷文件',
  making: '制作中',
  shipped: '已发货',
  refund_processing: '退款处理中',
  refunded: '已退款',
  cancelled: '已取消'
};

const STATUS_TIPS: StatusMap = {
  paid: '请点击左侧 ⬇ 按钮下载 PDF',
  making: '相册通常会在付款后 3-5 个工作日内发货。',
  shipped: '相册已发出，请查看下方快递公司和快递单号。',
  refund_processing: '退款将在 1-2 个工作日内处理。',
  refunded: '退款已完成，积分已返还。'
};

const TYPE_MAP: StatusMap = { pdf: 'PDF', print: '实体相册' };

// =============================================================================
// Address Picker
// =============================================================================

function buildAddressPickerHTML(
  selectedProvince: string,
  selectedCity: string,
  selectedDistrict: string
): string {
  return '<div class="form-row"><label>收件人姓名</label><input class="form-input" id="recpName" placeholder="姓名"></div>' +
    '<div class="form-row"><label>手机号码</label><input class="form-input" id="recpPhone" placeholder="手机号"></div>' +
    '<div class="addr-picker">' +
    '<select id="selProvince"><option value="">-- 省份 --</option></select>' +
    '<select id="selCity"><option value="">-- 城市 --</option></select>' +
    '<select id="selDistrict"><option value="">-- 区县 --</option></select>' +
    '</div>' +
    '<div class="form-row"><label>详细地址</label><input class="form-input" id="recpAddr" placeholder="街道、门牌号等"></div>';
}

function populateProvinceSelect(
  selectEl: HTMLSelectElement,
  regions: ChinaRegions,
  selected: string
): void {
  selectEl.innerHTML = '<option value="">-- 省份 --</option>';
  regions.filter(function(p: Province) { return EXCLUDED_PROVINCES.indexOf(p.name) === -1; }).forEach(function(prov: Province) {
    const opt = document.createElement('option');
    opt.value = prov.name;
    opt.textContent = prov.name;
    if (selected === prov.name) opt.selected = true;
    selectEl.appendChild(opt);
  });
}

function populateCitySelect(
  citySelect: HTMLSelectElement,
  districtSelect: HTMLSelectElement,
  regions: ChinaRegions,
  provinceName: string,
  selectedCity: string | null,
  selectedDistrict: string | null
): void {
  citySelect.innerHTML = '<option value="">-- 城市 --</option>';
  districtSelect.innerHTML = '<option value="">-- 区县 --</option>';
  if (!provinceName) return;
  const prov = regions.find(function(p: Province) { return p.name === provinceName; });
  if (!prov || !prov.children) return;
  prov.children.forEach(function(city: City) {
    const opt = document.createElement('option');
    opt.value = city.name;
    opt.textContent = city.name;
    if (selectedCity === city.name) opt.selected = true;
    citySelect.appendChild(opt);
  });
  if (selectedCity) {
    const cityObj = prov.children.find(function(c: City) { return c.name === selectedCity; });
    if (cityObj && cityObj.children) {
      cityObj.children.forEach(function(dist: District) {
        const opt = document.createElement('option');
        opt.value = dist.name;
        opt.textContent = dist.name;
        if (selectedDistrict === dist.name) opt.selected = true;
        districtSelect.appendChild(opt);
      });
    }
  }
}

function setupAddressPickerEvents(modalEl: HTMLElement): void {
  const regions = (window as any)._chinaRegions as ChinaRegions | null;
  if (!regions) return;
  const selProvince = modalEl.querySelector('#selProvince') as HTMLSelectElement;
  const selCity = modalEl.querySelector('#selCity') as HTMLSelectElement;
  const selDistrict = modalEl.querySelector('#selDistrict') as HTMLSelectElement;
  selProvince.addEventListener('change', function(this: HTMLSelectElement) {
    populateCitySelect(selCity, selDistrict, regions, this.value, null, null);
  });
  selCity.addEventListener('change', function(this: HTMLSelectElement) {
    const provinceName = selProvince.value;
    const cityName = this.value;
    selDistrict.innerHTML = '<option value="">-- 区县 --</option>';
    if (!provinceName || !cityName) return;
    const prov = regions.find(function(p: Province) { return p.name === provinceName; });
    if (!prov || !prov.children) return;
    const city = prov.children.find(function(c: City) { return c.name === cityName; });
    if (city && city.children) {
      (city.children as District[]).forEach(function(dist: District) {
        const opt = document.createElement('option');
        opt.value = dist.name;
        opt.textContent = dist.name;
        selDistrict.appendChild(opt);
      });
    }
  });
}

export function showAddressModal(current: Partial<Address> | null): Promise<AddressSnapshot | null> {
  return new Promise(function(resolve) {
    loadChinaRegions().then(function(regions) {
      // Store globally so setupAddressPickerEvents can access it
      (window as any)._chinaRegions = regions;

      const overlay = document.createElement('div');
      overlay.className = 'publish-overlay';
      const pickerHTML = buildAddressPickerHTML(
        current && current.province ? current.province : '',
        current && current.city ? current.city : '',
        current && current.district ? current.district : ''
      );
      overlay.innerHTML =
        '<div class="publish-dialog" style="width:460px;">' +
        '<h3>' + (current && (current.province || current.address) ? '修改地址' : '新增地址') + '</h3>' +
        pickerHTML +
        '<div class="actions" style="margin-top:12px;">' +
        '<button id="addrModalCancel">取消</button>' +
        '<button id="addrModalConfirm" class="btn-accent">确定</button>' +
        '</div></div>';
      document.body.appendChild(overlay);

      populateProvinceSelect(overlay.querySelector('#selProvince') as HTMLSelectElement, regions, current && current.province ? current.province : '');
      const city = current && current.city ? current.city : '';
      const district = current && current.district ? current.district : '';
      if (current && current.province) {
        populateCitySelect(
          overlay.querySelector('#selCity') as HTMLSelectElement,
          overlay.querySelector('#selDistrict') as HTMLSelectElement,
          regions, current.province, city, district
        );
      }
      setupAddressPickerEvents(overlay);
      if (current && current.detail) {
        (overlay.querySelector('#recpAddr') as HTMLInputElement).value = current.detail;
      } else if (current && current.address && !current.province) {
        (overlay.querySelector('#recpAddr') as HTMLInputElement).value = current.address;
      }
      if (current && current.name) {
        (overlay.querySelector('#recpName') as HTMLInputElement).value = current.name;
      }
      if (current && current.phone) {
        (overlay.querySelector('#recpPhone') as HTMLInputElement).value = current.phone;
      }

      (overlay.querySelector('#addrModalCancel') as HTMLElement).addEventListener('click', function() { overlay.remove(); resolve(null); });
      overlay.addEventListener('click', function(e: MouseEvent) { if (e.target === overlay) { overlay.remove(); resolve(null); } });
      (overlay.querySelector('#addrModalConfirm') as HTMLElement).addEventListener('click', function() {
        const name = (overlay.querySelector('#recpName') as HTMLInputElement).value.trim();
        const phone = (overlay.querySelector('#recpPhone') as HTMLInputElement).value.trim();
        const province = (overlay.querySelector('#selProvince') as HTMLSelectElement).value;
        const city = (overlay.querySelector('#selCity') as HTMLSelectElement).value;
        const district = (overlay.querySelector('#selDistrict') as HTMLSelectElement).value;
        const detail = (overlay.querySelector('#recpAddr') as HTMLInputElement).value.trim();
        if (!name) { alert('请填写收件人姓名'); return; }
        if (!phone) { alert('请填写手机号码'); return; }
        if (!/^1\d{10}$/.test(phone)) { alert('请填写正确的11位手机号码'); return; }
        if (!province) { alert('请选择省份'); return; }
        if (!city) { alert('请选择城市'); return; }
        if (!district) { alert('请选择区县'); return; }
        if (!detail) { alert('请填写详细地址'); return; }
        overlay.remove();
        resolve({ name: name, phone: phone, province: province, city: city, district: district, detail: detail });
      });
    });
  });
}

function buildAddressSnapshot(): string {
  const province = document.getElementById('selProvince') as HTMLSelectElement | null;
  const city = document.getElementById('selCity') as HTMLSelectElement | null;
  const district = document.getElementById('selDistrict') as HTMLSelectElement | null;
  return JSON.stringify({
    name: (document.getElementById('recpName') as HTMLInputElement).value,
    phone: (document.getElementById('recpPhone') as HTMLInputElement).value,
    province: province ? province.value : '',
    city: city ? city.value : '',
    district: district ? district.value : '',
    detail: (document.getElementById('recpAddr') as HTMLInputElement).value
  });
}

// =============================================================================
// Order Calculations
// =============================================================================

function calcInternalSpreadCount(totalSpreads: number): number {
  if (!totalSpreads || totalSpreads <= 0) return 0;
  let count = totalSpreads;
  if (count >= 1) count--;
  if (count >= 1) count--;
  return Math.max(0, count);
}

// =============================================================================
// Payment
// =============================================================================

interface PaymentHandler {
  (orderData: PaymentInfo): Promise<PaymentResult>;
}

const PAYMENT_HANDLERS: Record<string, PaymentHandler> = {
  wechat: function(orderData: PaymentInfo) {
    return showQRCodePayment(orderData);
  },
  alipay: function(orderData: PaymentInfo) {
    return showQRCodePayment(orderData);
  }
};

function showQRCodePayment(orderData: PaymentInfo): Promise<PaymentResult> {
  return new Promise(function(resolve, reject) {
    fetch('/api/orders', {
      method: 'POST',
      credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData)
    })
    .then(function(r) { return r.json().then(function(data: OrderCreateResponse) { return { ok: r.ok, data: data }; }); })
    .then(function(result: { ok: boolean; data: OrderCreateResponse }) {
      if (!result.ok) { reject(new Error((result.data as any).message || '创建订单失败')); return; }
      const order = result.data as OrderCreateResponse;

      trackEvent('order_create_success', { order_id: order.id, album_id: order.album_id, published_template_id: order.published_template_id || null, amount: parseFloat(order.amount), credits_used: order.credits_used || 0 }, { immediate: true });

      if (order.status === 'paid') {
        trackEvent('payment_success', { order_id: order.id, album_id: order.album_id, published_template_id: order.published_template_id || null, amount: parseFloat(order.amount || '0') }, { immediate: true });
        resolve({ success: true, order: order }); return;
      }

      return fetch('/api/orders/' + order.id + '/create-payment', {
        method: 'POST',
        credentials: 'include'
      })
      .then(function(r2) { return r2.json().then(function(data2: any) { return { ok: r2.ok, data: data2 }; }); })
      .then(function(payResult: { ok: boolean; data: any }) {
        if (!payResult.ok) {
          trackEvent('payment_failed', { order_id: order && order.id, error_type: payResult.data.message || '创建支付失败' }, { immediate: true });
          reject(new Error(payResult.data.message || '创建支付失败')); return;
        }
        if (payResult.data.auto_paid) {
          trackEvent('payment_success', { order_id: order.id, album_id: order.album_id, published_template_id: order.published_template_id || null, amount: parseFloat(order.amount || '0') }, { immediate: true });
          resolve({ success: true, order: order }); return;
        }

        trackEvent('payment_qr_show', { order_id: order.id, album_id: order.album_id, published_template_id: order.published_template_id || null, amount: parseFloat(order.amount) }, { immediate: true });
        showQRModal(order.id, order.amount, payResult.data.code_url, function(paid) {
          if (paid) {
            resolve({ success: true, order: order });
          } else {
            // Cancel: delete the pending order silently
            fetch('/api/orders/' + order.id, {
              method: 'DELETE',
              credentials: 'include'
            }).catch(function() {});
            resolve({ cancelled: true });
          }
        });
      });
    })
    .catch(function(err) { reject(err); });
  });
}

let _pollTimer: ReturnType<typeof setInterval> | null = null;

function showQRModal(
  orderId: string,
  amount: string | number,
  codeUrl: string,
  callback: (paid: boolean) => void
): void {
  const overlay = document.getElementById('orderModalOverlay')!;
  const modal = document.getElementById('orderModal')!;
  overlay.classList.remove('hidden');

  modal.innerHTML =
    '<div class="qr-pay-modal">' +
      '<div class="qr-title">微信扫码支付</div>' +
      '<div class="qr-amount">¥' + parseFloat(String(amount)).toFixed(2) + '</div>' +
      '<div class="qr-container"><div id="payQRCode"></div></div>' +
      '<div class="qr-hint">请使用微信扫描二维码完成支付</div>' +
      '<div class="qr-cancel" id="qrCancelBtn">取消支付</div>' +
    '</div>';

  new QRCode(document.getElementById('payQRCode')!, {
    text: codeUrl,
    width: 200,
    height: 200,
    colorDark: '#000000',
    colorLight: '#ffffff'
  });

  document.getElementById('qrCancelBtn')!.addEventListener('click', function() {
    if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
    overlay.classList.add('hidden');
    trackEvent('payment_cancel', { order_id: orderId }, { immediate: true });
    callback(false);
  });

  _pollTimer = setInterval(function() {
    fetch('/api/orders/' + orderId, {
      credentials: 'include'
    })
    .then(function(r) { return r.json(); })
    .then(function(order: any) {
      if (order.status === 'paid') {
        clearInterval(_pollTimer);
        _pollTimer = null;
        overlay.classList.add('hidden');
        showToast('支付成功');
        trackEvent('payment_success', { order_id: orderId, album_id: order.album_id || null, published_template_id: order.published_template_id || null, amount: parseFloat(order.amount || amount || '0') }, { immediate: true });
        callback(true);
      }
    })
    .catch(function() {});
  }, 3000);
}

export function processPayment(paymentInfo: PaymentInfo): Promise<PaymentResult> {
  const handler = PAYMENT_HANDLERS[paymentInfo.payMethod] || showQRCodePayment;
  return Promise.resolve(handler(paymentInfo));
}

// =============================================================================
// Order Modal
// =============================================================================

let _orderState: OrderState | null = null;
let _orderOverlayHandler: ((e: MouseEvent) => void) | null = null;

export async function openUnifiedOrderModal(albumId: string | number): Promise<void> {
  albumId = parseInt(String(albumId));

  let albumDetail: AlbumDetail;
  let userAlbums: Album[];
  try {
    albumDetail = await loadAlbumFromDB(albumId);
    userAlbums = await loadUserAlbums();
    await Promise.all([loadTemplateRegistry(), _fetchPricing()]);
  } catch(e) {
    showToast('加载相册数据失败');
    return;
  }

  const album = userAlbums.find(function(a: Album) { return a.id === albumId; });
  if (!album) { showToast('相册不存在'); return; }

  const totalSpreads = Object.keys(albumDetail.spreads || {}).length;
  const internalPages = calcInternalSpreadCount(totalSpreads) * 2;
  const defaultPhysicalType = PHYSICAL_ALBUM_TYPES[0]; // hardcover-hard
  const unitPrice = calculatePhysicalPrice(defaultPhysicalType, internalPages);

  const previewPages = _buildOrderPreviewPages(albumDetail);

  _orderState = {
    albumId: albumId,
    albumDetail: albumDetail,
    album: album,
    selectedTab: 'pdf',
    previewIndex: 0,
    previewPages: previewPages,
    physUnitPrice: unitPrice,
    physInternalPages: internalPages,
    totalSpreads: totalSpreads,
    selectedPhysicalType: 'hardcover-hard'
  };

  const modal = document.getElementById('orderModal')!;
  modal.innerHTML =
    '<div class="modal-header">' +
      '<h3>下单</h3>' +
      '<button class="modal-close" id="orderModalClose">&times;</button>' +
    '</div>' +
    '<div class="order-preview-area" id="orderPreviewArea"></div>' +
    '<div class="order-modal-body-split">' +
      '<div class="order-sidebar" id="orderSidebar"></div>' +
      '<div class="order-modal-detail" id="orderDetail"></div>' +
    '</div>';

  document.getElementById('orderModalOverlay')!.classList.remove('hidden');
  modal.classList.add('order-modal-wide');

  await _renderOrderPreview();
  _renderOrderSidebar();
  _renderOrderDetail();

  document.getElementById('orderModalClose')!.addEventListener('click', closeOrderModal);
  _orderOverlayHandler = function(e: MouseEvent) {
    if (e.target === document.getElementById('orderModalOverlay')) closeOrderModal();
  };
  document.getElementById('orderModalOverlay')!.addEventListener('click', _orderOverlayHandler);
}

function _getOrderPreviewIndicator(): string {
  const pages = _orderState!.previewPages;
  const idx = _orderState!.previewIndex;
  const current = pages[idx];
  const innerSpreads = pages.filter(function(p: OrderPreviewPage) { return p.type !== 'cover' && p.type !== 'back'; });
  const totalInnerPages = innerSpreads.length * 2;
  if (current.type === 'cover') return '封面';
  if (current.type === 'back') return '封底';
  const innerIdx = innerSpreads.indexOf(current);
  const startPage = innerIdx * 2 + 1;
  return '第' + startPage + '-' + (startPage + 1) + ' / 共' + totalInnerPages + '页';
}

async function _renderOrderPreview(): Promise<void> {
  const container = document.getElementById('orderPreviewArea');
  if (!container || !_orderState) return;

  const pages = _orderState.previewPages;
  const idx = _orderState.previewIndex;
  if (pages.length === 0) {
    container.innerHTML = '<div style="text-align:center;padding:40px;color:var(--text-dim);">暂无预览</div>';
    return;
  }

  const isFirst = idx === 0;
  const isLast = idx === pages.length - 1;

  container.innerHTML =
    '<div class="order-preview-nav">' +
      '<button class="order-preview-nav-arrow" id="orderPreviewPrev" ' + (isFirst ? 'disabled' : '') + '>&#9664;</button>' +
      '<div class="order-preview-container" id="orderPreviewFrame"></div>' +
      '<button class="order-preview-nav-arrow" id="orderPreviewNext" ' + (isLast ? 'disabled' : '') + '>&#9654;</button>' +
    '</div>' +
    '<div class="order-page-indicator">' + _getOrderPreviewIndicator() + '</div>';

  try {
    const pagesHtml = await _renderOrderPreviewPages(pages[idx].spread);
    const frame = document.getElementById('orderPreviewFrame');
    if (frame) frame.innerHTML = pagesHtml;
  } catch(e) {
    console.warn('Order preview render error:', e);
  }

  document.getElementById('orderPreviewPrev')!.addEventListener('click', function() {
    if (_orderState!.previewIndex > 0) { _orderState!.previewIndex--; _updateOrderPreviewPage(); }
  });
  document.getElementById('orderPreviewNext')!.addEventListener('click', function() {
    if (_orderState!.previewIndex < _orderState!.previewPages.length - 1) { _orderState!.previewIndex++; _updateOrderPreviewPage(); }
  });
}

function _updateOrderPreviewPage(): void {
  const pages = _orderState!.previewPages;
  const idx = _orderState!.previewIndex;
  const prevBtn = document.getElementById('orderPreviewPrev') as HTMLButtonElement | null;
  const nextBtn = document.getElementById('orderPreviewNext') as HTMLButtonElement | null;
  const indicator = document.querySelector('.order-page-indicator');
  if (prevBtn) { prevBtn.disabled = idx === 0; }
  if (nextBtn) { nextBtn.disabled = idx >= pages.length - 1; }
  if (indicator) { indicator.textContent = _getOrderPreviewIndicator(); }

  _renderOrderPreviewPages(pages[idx].spread).then(function(pagesHtml: string) {
    const frame = document.getElementById('orderPreviewFrame');
    if (frame) frame.innerHTML = pagesHtml;
  });
}

function _renderOrderSidebar(): void {
  const sidebar = document.getElementById('orderSidebar');
  if (!sidebar || !_orderState) return;

  const selected = _orderState.selectedTab;

  sidebar.innerHTML =
    '<button class="order-cat-btn' + (selected === 'pdf' ? ' active' : '') + '" data-tab="pdf">PDF 电子版</button>' +
    '<div style="position:relative;">' +
      '<button class="order-cat-btn' + (selected === 'print' ? ' active' : '') + '" data-tab="print">8寸方形精装版</button>' +
      '<span class="order-cat-badge">推荐</span>' +
    '</div>';

  sidebar.querySelectorAll('.order-cat-btn').forEach(function(btn: Element) {
    btn.addEventListener('click', function(this: HTMLElement) {
      const tab = this.getAttribute('data-tab');
      if (_orderState!.selectedTab === tab) return;
      _orderState!.selectedTab = tab as 'pdf' | 'print';
      _renderOrderSidebar();
      _renderOrderDetail();
    });
  });
}

function _renderOrderDetail(): void {
  if (!_orderState) return;
  if (_orderState.selectedTab === 'pdf') {
    _renderPDFDetail();
  } else {
    _renderPhysicalDetail();
  }
}

function _renderPDFDetail(): void {
  const detail = document.getElementById('orderDetail');
  if (!detail || !_orderState) return;

  const html =
    '<div class="order-modal-detail-content">' +
      '<div class="order-price-card">' +
        '<div>PDF 电子版</div>' +
        '<div>60 页内免费 · 超出可打赏解锁</div>' +
      '</div>' +
      '<div class="order-qty-row">' +
        '<div class="order-qty-group">' +
          '<div style="text-align:center;">' +
            '<input type="number" class="order-credits-input" id="pdfCustomAmount" value="0" min="0" max="999" step="1" placeholder="0" style="text-align:center;font-size:24px;width:160px;padding:10px 12px;font-weight:700;">' +
          '</div>' +
          '<div style="text-align:center;margin-top:6px;font-size:12px;color:var(--text-dim);">打赏金额（1 元为单位，默认免费）</div>' +
          '<div style="text-align:center;margin-top:4px;font-weight:700;color:var(--text-primary);font-size:13px;">感谢您的支持 ❤️</div>' +
        '</div>' +
      '</div>' +
    '</div>' +
    '<div class="order-check-row">' +
      '<input type="checkbox" id="pdfTermsCheck">' +
      '<span class="order-terms-text">我已检查全部页面并确认信息无误，下单即表示同意<a href="terms.html" target="_blank" style="color:var(--accent);text-decoration:underline;">《用户协议》</a></span>' +
    '</div>' +
    '<div class="order-modal-detail-footer">' +
      '<div>' +
        '<span style="font-size:13px;">实付：<strong style="color:var(--accent);" id="pdfFinalAmount">¥0.00</strong></span>' +
        '<span style="font-size:10px;color:var(--text-dim);margin-left:8px;" id="pdfBreakdown">免费下载</span>' +
      '</div>' +
      '<div style="display:flex;gap:6px;">' +
        '<button class="btn btn-accent" id="pdfConfirmBtn">确认下单</button>' +
      '</div>' +
    '</div>';

  detail.innerHTML = html;

  function getUnitPrice(): number {
    const input = document.getElementById('pdfCustomAmount') as HTMLInputElement;
    if (!input) return 0;
    let val = parseFloat(input.value) || 0;
    if (val < 0) val = 0;
    if (val > 999) val = 999;
    return Math.floor(val);
  }

  // PDF page gate (mirrors server validatePdfOrderInput):
  //   <=60 free; 60< <=100 requires tip; >100 rejected.
  function getPdfPageGate(): { blocked: boolean; reason: string } {
    const internalSpreads = calcInternalSpreadCount(_orderState!.totalSpreads);
    const pageCount = internalSpreads * 2;
    const tip = getUnitPrice();
    if (pageCount > 100) {
      return { blocked: true, reason: 'PDF 最多 100 页，请返回编辑器删减' };
    }
    if (pageCount > 60 && tip <= 0) {
      return { blocked: true, reason: '超出 60 页，打赏任意金额即可下单' };
    }
    return { blocked: false, reason: '' };
  }

  function updatePDFTotal(): void {
    const unitPrice = getUnitPrice();
    const finalEl = document.getElementById('pdfFinalAmount') as HTMLElement;
    const breakdownEl = document.getElementById('pdfBreakdown') as HTMLElement;
    const confirmBtn = document.getElementById('pdfConfirmBtn') as HTMLButtonElement | null;
    const gate = getPdfPageGate();

    if (gate.blocked) {
      finalEl.textContent = '—';
      breakdownEl.textContent = gate.reason;
      breakdownEl.style.color = '#e53e3e';
      if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.style.opacity = '0.5'; }
      return;
    }
    breakdownEl.style.color = '';
    if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.style.opacity = ''; }
    if (unitPrice <= 0) {
      finalEl.textContent = '免费';
      breakdownEl.textContent = '无需支付，直接下载';
    } else {
      finalEl.textContent = '¥' + unitPrice.toFixed(2);
      breakdownEl.textContent = '打赏金额 ¥' + unitPrice.toFixed(2);
    }
  }

  (document.getElementById('pdfCustomAmount') as HTMLInputElement).addEventListener('input', function(this: HTMLInputElement) {
    let val = parseFloat(this.value) || 0;
    if (val < 0) { val = 0; this.value = '0'; }
    if (val > 999) { val = 999; this.value = '999'; }
    updatePDFTotal();
  });

  updatePDFTotal();

  (document.getElementById('pdfConfirmBtn') as HTMLElement).addEventListener('click', function(this: HTMLButtonElement) {
    if (!(document.getElementById('pdfTermsCheck') as HTMLInputElement).checked) {
      showToast('请先勾选确认项', { red: true });
      return;
    }
    // Defensive: button is disabled while blocked, but re-check before any request.
    const gate = getPdfPageGate();
    if (gate.blocked) {
      showWarning(gate.reason);
      return;
    }
    const btn = this;
    btn.disabled = true;
    btn.style.opacity = '0.5';

    const unitPrice = getUnitPrice();
    const internalSpreads = calcInternalSpreadCount(_orderState!.totalSpreads);

    processPayment({
      payMethod: 'wechat',
      album_id: _orderState!.albumId,
      product_type: 'pdf',
      page_count: internalSpreads * 2,
      credits_used: 0,
      custom_amount: unitPrice
    }).then(function(result: PaymentResult) {
      if ('cancelled' in result) { closeOrderModal(); return; }
      fetch('/api/albums/' + _orderState!.albumId, {
        method: 'PUT',
        credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed', title: _orderState!.album.title })
      }).catch(function() {});
      invalidateAlbumsCache();
      closeOrderModal();
      renderView('orders');
      document.querySelectorAll('.menu-item').forEach(function(m: Element) { m.classList.remove('active'); });
      const ordersBtn = document.querySelector('.menu-item[data-view="orders"]');
      if (ordersBtn) ordersBtn.classList.add('active');
      setCurrentView('orders');
    }).catch(function(err: any) {
      showToast(err.message || '支付失败');
      btn.disabled = false;
      btn.style.opacity = '';
    });
  });
}

function _renderPhysicalTypeCards(): string {
  if (!_orderState) return '';

  const selectedType = _orderState.selectedPhysicalType;
  const internalPages = _orderState.physInternalPages;
  let cardsHtml = '<div class="physical-type-cards">';

  PHYSICAL_ALBUM_TYPES.forEach(function(type: PhysicalAlbumType) {
    const isSelected = type.id === selectedType;
    const extraPages = Math.max(0, internalPages - type.basePages);
    const extraUnits = extraPages > 0 ? Math.ceil(extraPages / type.extraPagesUnit) : 0;
    const extraPrice = extraUnits * type.extraPricePerPage;
    const totalPrice = type.basePrice + extraPrice;
    const overLimit = internalPages > type.maxPages;

    cardsHtml +=
      '<div class="physical-type-card' + (isSelected ? ' selected' : '') + (overLimit ? ' disabled' : '') + '" data-type="' + type.id + '"' + (overLimit ? ' style="opacity:0.5;"' : '') + '>' +
        '<div class="physical-type-card-title">' + type.name + '</div>' +
        '<div class="physical-type-card-desc">' + type.description + '</div>' +
        (overLimit
          ? '<div class="physical-type-card-price" style="color:#e53e3e;">超过上限 ' + type.maxPages + ' 内页</div>'
          : '<div class="physical-type-card-price">¥' + totalPrice + ' · 含' + internalPages + '页</div>' +
            (extraPages > 0 ? '<div class="physical-type-card-extra">超出基础' + extraPages + '页（+' + extraPrice + '元）</div>' : '')) +
      '</div>';
  });

  cardsHtml += '</div>';
  return cardsHtml;
}

function _renderPhysicalDetail(): void {
  const detail = document.getElementById('orderDetail');
  if (!detail || !_orderState) return;

  const selectedType = PHYSICAL_ALBUM_TYPES.find(function(t: PhysicalAlbumType) {
    return t.id === _orderState!.selectedPhysicalType;
  });
  if (!selectedType) return;

  const internalPages = _orderState.physInternalPages;
  const unitPrice = calculatePhysicalPrice(selectedType, internalPages);

  if (internalPages < selectedType.basePages) {
    detail.innerHTML = '<div class="order-modal-detail-content" style="display:flex;align-items:center;justify-content:center;"><p style="color:#e53e3e;font-size:14px;font-weight:700;">实体相册最少 ' + selectedType.basePages + ' 内页，请继续添加页面。</p></div>';
    return;
  }
  if (internalPages > selectedType.maxPages) {
    // Selected type's cap exceeded — auto-switch to a type that fits
    // (e.g. hardcover-hard 60 -> hardcover-soft 120) instead of hard-blocking.
    const fitType = PHYSICAL_ALBUM_TYPES.find(function(t: PhysicalAlbumType) {
      return internalPages >= t.basePages && internalPages <= t.maxPages;
    });
    if (fitType && fitType.id !== selectedType.id) {
      _orderState!.selectedPhysicalType = fitType.id;
      _renderPhysicalDetail();
      return;
    }
    // No type accommodates this page count — report the global max.
    const globalMaxPages = PHYSICAL_ALBUM_TYPES.reduce(function(m: number, t: PhysicalAlbumType) { return Math.max(m, t.maxPages); }, 0);
    detail.innerHTML = '<div class="order-modal-detail-content" style="display:flex;align-items:center;justify-content:center;"><p style="color:#e53e3e;font-size:14px;font-weight:700;">实体相册最多 ' + globalMaxPages + ' 内页，请删减页面后再下单。</p></div>';
    return;
  }
  if (internalPages % 2 !== 0) {
    detail.innerHTML = '<div class="order-modal-detail-content" style="display:flex;align-items:center;justify-content:center;"><p style="color:#e53e3e;font-size:14px;font-weight:700;">实体相册内页必须为偶数页，请补齐 1 页。</p></div>';
    return;
  }

  const addresses = loadAddresses();
  const hasAddresses = addresses.length > 0;

  let addrOptions = '';
  addresses.forEach(function(addr: Address, i: number) {
    const label = (addr.name || '未填姓名') + ' · ' + (addr.phone || '未填电话') + ' · ' + addr.province + addr.city + addr.district + ' ' + addr.detail;
    addrOptions += '<option value="' + i + '">' + label + '</option>';
  });
  addrOptions += '<option value="new">+ 新增地址</option>';

  const typeCardsHtml = _renderPhysicalTypeCards();

  const html =
    '<div class="order-modal-detail-content">' +
      typeCardsHtml +
      '<div class="order-qty-label">收件地址</div>' +
      '<select class="order-address-select" id="physAddrSelect">' + addrOptions + '</select>' +
      '<div id="physAddrForm"></div>' +
      '<div class="order-qty-row">' +
        '<div class="order-qty-group">' +
          '<div class="order-qty-label">购买数量</div>' +
          '<div class="order-qty-controls">' +
            '<button class="order-qty-btn" id="physQtyMinus">-</button>' +
            '<span class="order-qty-value" id="physQtyDisplay">1</span>' +
            '<button class="order-qty-btn" id="physQtyPlus">+</button>' +
            '<span style="font-size:10px;color:var(--text-dim);margin-left:4px;">最多100本</span>' +
          '</div>' +
        '</div>' +
        '<div class="order-qty-group">' +
          '<div class="order-qty-label">积分抵扣<span style="font-weight:400;opacity:0.6;">（最多抵20%）</span></div>' +
          '<input type="number" class="order-credits-input" id="physCredits" value="0" min="0">' +
          '<span style="font-size:10px;color:var(--text-dim);" id="physCreditsInfo">加载中...</span>' +
        '</div>' +
      '</div>' +
    '</div>' +
    '<div class="order-check-row">' +
      '<input type="checkbox" id="physTermsCheck">' +
      '<span class="order-terms-text">我已检查全部页面并确认信息无误，下单即表示同意<a href="terms.html" target="_blank" style="color:var(--accent);text-decoration:underline;">《用户协议》</a></span>' +
    '</div>' +
    '<div class="order-modal-detail-footer">' +
      '<div>' +
        '<span style="font-size:13px;">实付：<strong style="color:var(--accent);" id="physFinalAmount">¥' + unitPrice.toFixed(2) + '</strong></span>' +
        '<span style="font-size:10px;color:var(--text-dim);margin-left:8px;" id="physBreakdown">单价 ¥' + unitPrice + ' ×1 · 抵扣 -¥0</span>' +
      '</div>' +
      '<button class="btn btn-accent" id="physPayBtn">确认付款</button>' +
    '</div>';

  detail.innerHTML = html;

  // Type selection card click events
  const typeCards = detail.querySelectorAll('.physical-type-card');
  typeCards.forEach(function(card: Element) {
    card.addEventListener('click', function(this: HTMLElement) {
      const typeId = this.getAttribute('data-type');
      if (_orderState!.selectedPhysicalType === typeId) return;
      const targetType = PHYSICAL_ALBUM_TYPES.find(function(t: PhysicalAlbumType) { return t.id === typeId; });
      if (targetType && _orderState!.physInternalPages < targetType.basePages) {
        showToast('当前相册仅 ' + _orderState!.physInternalPages + ' 内页，' + targetType.name + '需要至少 ' + targetType.basePages + ' 内页');
        return;
      }
      if (targetType && _orderState!.physInternalPages > targetType.maxPages) {
        showToast('当前相册 ' + _orderState!.physInternalPages + ' 内页，超过 ' + targetType.name + ' 上限 ' + targetType.maxPages + ' 内页');
        return;
      }
      _orderState!.selectedPhysicalType = typeId!;
      _renderPhysicalDetail();
    });
  });

  let physQty = 1;
  let physCredits = 0;
  let physCreditBalance = 0;
  let selectedAddrIndex: string | number = hasAddresses ? '0' : 'new';

  function _populateDistrictSelect(
    distSel: HTMLSelectElement,
    provName: string,
    cityName: string,
    regions: ChinaRegions
  ): void {
    distSel.innerHTML = '<option value="">区县</option>';
    if (!provName || !cityName) return;
    const prov = regions.find(function(p: Province) { return p.name === provName; });
    if (!prov || !prov.children) return;
    const city = prov.children.find(function(c: City) { return c.name === cityName; });
    if (city && city.children) {
      (city.children as District[]).forEach(function(dist: District) {
        const opt = document.createElement('option');
        opt.value = dist.name;
        opt.textContent = dist.name;
        distSel.appendChild(opt);
      });
    }
  }

  function updateAddrForm(): void {
    const formContainer = document.getElementById('physAddrForm');
    if (!formContainer) return;
    const val = (document.getElementById('physAddrSelect') as HTMLSelectElement).value;
    selectedAddrIndex = val;

    if (val === 'new') {
      formContainer.innerHTML =
        '<div class="order-addr-field-row cols2">' +
          '<input class="order-addr-input" id="physName" placeholder="收件人姓名">' +
          '<input class="order-addr-input" id="physPhone" placeholder="手机号">' +
        '</div>' +
        '<div class="order-addr-field-row cols3">' +
          '<select class="order-addr-select-sm" id="physProvince"><option value="">省份</option></select>' +
          '<select class="order-addr-select-sm" id="physCity"><option value="">城市</option></select>' +
          '<select class="order-addr-select-sm" id="physDistrict"><option value="">区县</option></select>' +
        '</div>' +
        '<input class="order-addr-input" id="physDetailAddr" placeholder="详细地址（街道、门牌号等）" style="margin-bottom:6px;">' +
        '<div style="font-size:10px;color:var(--text-dim);margin-bottom:8px;">保存到账户地址，下次可直接选择</div>';

      loadChinaRegions().then(function(regions: ChinaRegions) {
        populateProvinceSelect(document.getElementById('physProvince') as HTMLSelectElement, regions, '');
        (document.getElementById('physProvince') as HTMLSelectElement).addEventListener('change', function(this: HTMLSelectElement) {
          populateCitySelect(
            document.getElementById('physCity') as HTMLSelectElement,
            document.getElementById('physDistrict') as HTMLSelectElement,
            regions, this.value, null, null
          );
        });
        (document.getElementById('physCity') as HTMLSelectElement).addEventListener('change', function(this: HTMLSelectElement) {
          const provName = (document.getElementById('physProvince') as HTMLSelectElement).value;
          const cityName = this.value;
          const distSel = document.getElementById('physDistrict') as HTMLSelectElement;
          _populateDistrictSelect(distSel, provName, cityName, regions);
        });
      });
    } else {
      // Existing address selected -- pre-fill form fields for editing
      const addrs = loadAddresses();
      const addrIdx = parseInt(val);
      const addr = (addrIdx >= 0 && addrIdx < addrs.length) ? addrs[addrIdx] : null;

      formContainer.innerHTML =
        '<div class="order-addr-field-row cols2">' +
          '<input class="order-addr-input" id="physName" placeholder="收件人姓名" value="' + escapeHtml(addr ? addr.name || '' : '') + '">' +
          '<input class="order-addr-input" id="physPhone" placeholder="手机号" value="' + escapeHtml(addr ? addr.phone || '' : '') + '">' +
        '</div>' +
        '<div class="order-addr-field-row cols3">' +
          '<select class="order-addr-select-sm" id="physProvince"><option value="">省份</option></select>' +
          '<select class="order-addr-select-sm" id="physCity"><option value="">城市</option></select>' +
          '<select class="order-addr-select-sm" id="physDistrict"><option value="">区县</option></select>' +
        '</div>' +
        '<input class="order-addr-input" id="physDetailAddr" placeholder="详细地址（街道、门牌号等）" style="margin-bottom:6px;" value="' + escapeHtml(addr ? addr.detail || '' : '') + '">';

      loadChinaRegions().then(function(regions: ChinaRegions) {
        if (addr) {
          populateProvinceSelect(document.getElementById('physProvince') as HTMLSelectElement, regions, addr.province || '');
          populateCitySelect(
            document.getElementById('physCity') as HTMLSelectElement,
            document.getElementById('physDistrict') as HTMLSelectElement,
            regions, addr.province || '', addr.city || '', addr.district || ''
          );
        } else {
          populateProvinceSelect(document.getElementById('physProvince') as HTMLSelectElement, regions, '');
        }
        (document.getElementById('physProvince') as HTMLSelectElement).addEventListener('change', function(this: HTMLSelectElement) {
          populateCitySelect(
            document.getElementById('physCity') as HTMLSelectElement,
            document.getElementById('physDistrict') as HTMLSelectElement,
            regions, this.value, null, null
          );
        });
        (document.getElementById('physCity') as HTMLSelectElement).addEventListener('change', function(this: HTMLSelectElement) {
          const provName = (document.getElementById('physProvince') as HTMLSelectElement).value;
          const cityName = this.value;
          const distSel = document.getElementById('physDistrict') as HTMLSelectElement;
          _populateDistrictSelect(distSel, provName, cityName, regions);
        });
      });
    }
  }

  (document.getElementById('physAddrSelect') as HTMLSelectElement).value = hasAddresses ? '0' : 'new';
  updateAddrForm();
  (document.getElementById('physAddrSelect') as HTMLSelectElement).addEventListener('change', updateAddrForm);

  function updatePhysTotal(): void {
    const qty = physQty;
    const credits = physCredits;
    const totalRaw = unitPrice * qty;
    const pctMax = Math.floor(totalRaw * 0.20 * _creditsPerYuan);
    const maxCredits = Math.min(pctMax, physCreditBalance || Infinity);
    if (credits > maxCredits) {
      physCredits = maxCredits;
      (document.getElementById('physCredits') as HTMLInputElement).value = String(physCredits);
    }
    const final = Math.max(0, totalRaw - physCredits / _creditsPerYuan);
    (document.getElementById('physFinalAmount') as HTMLElement).textContent = '¥' + final.toFixed(2);
    (document.getElementById('physBreakdown') as HTMLElement).textContent = '单价 ¥' + unitPrice + ' ×' + qty + ' · 抵扣 -¥' + (physCredits / _creditsPerYuan).toFixed(2);
  }

  (document.getElementById('physQtyMinus') as HTMLElement).addEventListener('click', function() {
    if (physQty > 1) { physQty--; (document.getElementById('physQtyDisplay') as HTMLElement).textContent = String(physQty); updatePhysTotal(); }
  });
  (document.getElementById('physQtyPlus') as HTMLElement).addEventListener('click', function() {
    if (physQty < 100) { physQty++; (document.getElementById('physQtyDisplay') as HTMLElement).textContent = String(physQty); updatePhysTotal(); }
  });
  (document.getElementById('physCredits') as HTMLInputElement).addEventListener('input', function(this: HTMLInputElement) {
    let val = Math.max(0, parseInt(this.value) || 0);
    if (val > physCreditBalance) val = physCreditBalance;
    const pctMax2 = Math.floor(unitPrice * physQty * 0.20 * _creditsPerYuan);
    if (val > pctMax2) val = pctMax2;
    this.value = String(val);
    physCredits = val;
    updatePhysTotal();
  });

  fetch('/api/credits', {
    credentials: 'include'
  }).then(function(r) { return r.json(); }).then(function(data: any) {
    const balance = data.balance || 0;
    physCreditBalance = balance;
    const input = document.getElementById('physCredits') as HTMLInputElement;
    input.max = String(balance);
    (document.getElementById('physCreditsInfo') as HTMLElement).textContent = '可用 ' + balance + ' 积分';
    updatePhysTotal();
  }).catch(function() {
    (document.getElementById('physCreditsInfo') as HTMLElement).textContent = '积分加载失败';
  });

  (document.getElementById('physTermsCheck') as HTMLElement).addEventListener('change', function() {
  });

  (document.getElementById('physPayBtn') as HTMLElement).addEventListener('click', function(this: HTMLButtonElement) {
    if (!(document.getElementById('physTermsCheck') as HTMLInputElement).checked) {
      showToast('请先勾选确认项', { red: true });
      return;
    }
    const btn = this;
    btn.disabled = true;
    btn.style.opacity = '0.5';
    btn.textContent = '创建支付中...';

    const qty = physQty;
    let credits = physCredits;
    const totalRaw = unitPrice * qty;
    const pctMax = Math.floor(totalRaw * 0.20 * _creditsPerYuan);
    const maxCredits = Math.min(pctMax, physCreditBalance || Infinity);
    if (credits > maxCredits) credits = maxCredits;
    const finalAmount = Math.max(0, totalRaw - credits / _creditsPerYuan);

    let addressSnapshot: AddressSnapshot = {} as AddressSnapshot;
    let addressValid = true;

    // Always read from form fields (both new and existing addresses show the form)
    addressSnapshot = {
      name: ((document.getElementById('physName') as HTMLInputElement | null) ? (document.getElementById('physName') as HTMLInputElement).value : '').trim(),
      phone: ((document.getElementById('physPhone') as HTMLInputElement | null) ? (document.getElementById('physPhone') as HTMLInputElement).value : '').trim(),
      province: ((document.getElementById('physProvince') as HTMLSelectElement | null) ? (document.getElementById('physProvince') as HTMLSelectElement).value : '').trim(),
      city: ((document.getElementById('physCity') as HTMLSelectElement | null) ? (document.getElementById('physCity') as HTMLSelectElement).value : '').trim(),
      district: ((document.getElementById('physDistrict') as HTMLSelectElement | null) ? (document.getElementById('physDistrict') as HTMLSelectElement).value : '').trim(),
      detail: ((document.getElementById('physDetailAddr') as HTMLInputElement | null) ? (document.getElementById('physDetailAddr') as HTMLInputElement).value : '').trim()
    };

    if (!addressSnapshot.name || !addressSnapshot.phone || !addressSnapshot.province || !addressSnapshot.city || !addressSnapshot.district || !addressSnapshot.detail) {
      showToast('请填写完整的收件信息（姓名、手机号、省市区和详细地址为必填）');
      addressValid = false;
    } else if (!/^1\d{10}$/.test(addressSnapshot.phone)) {
      showToast('请填写正确的11位手机号码');
      addressValid = false;
    } else {
      const addrs = loadAddresses();
      if (selectedAddrIndex === 'new') {
        // Persist new address to server (fire-and-forget; don't block order)
        createAddressOnServer({
          name: addressSnapshot.name,
          phone: addressSnapshot.phone,
          province: addressSnapshot.province,
          city: addressSnapshot.city,
          district: addressSnapshot.district,
          detail: addressSnapshot.detail,
          isDefault: addrs.length === 0
        }).then(function(created) {
          addrs.push(created);
          saveAddressesToCache(addrs);
        }).catch(function(e) { console.warn('Failed to save new address to server:', e); });
      } else {
        const idx = parseInt(String(selectedAddrIndex));
        if (idx >= 0 && idx < addrs.length && addrs[idx].id) {
          // Update existing address on server (fire-and-forget)
          updateAddressOnServer(addrs[idx].id!, {
            name: addressSnapshot.name,
            phone: addressSnapshot.phone,
            province: addressSnapshot.province,
            city: addressSnapshot.city,
            district: addressSnapshot.district,
            detail: addressSnapshot.detail
          }).then(function() {
            Object.assign(addrs[idx], addressSnapshot);
            saveAddressesToCache(addrs);
          }).catch(function(e) { console.warn('Failed to update address on server:', e); });
        }
      }
    }

    if (!addressValid) {
      btn.disabled = false;
      btn.style.opacity = '';
      btn.textContent = '确认付款';
      return;
    }

    fetch('/api/orders/print/payment-intents', {
      method: 'POST',
      credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        album_id: _orderState!.albumId,
        page_count: _orderState!.physInternalPages,
        quantity: qty,
        credits_used: credits,
        product_type: _orderState!.selectedPhysicalType,
        address_snapshot: addressSnapshot,
        confirmation_snapshot: {
          album_title: _orderState!.album.title,
          confirmed_items: {
            pages_checked: true,
            custom_product_terms: true,
            address_checked: true,
            content_terms: true,
            no_returns_after_production: true
          }
        }
      })
    }).then(function(resp) { return resp.json().then(function(data: any) { return { ok: resp.ok, data: data }; }); }).then(function(result: { ok: boolean; data: any }) {
      if (result.data.error) {
        throw new Error(result.data.message || '创建支付失败');
      }
      if (result.data.payment_intent_id) {
        const albumId = _orderState!.albumId;
        const albumTitle = _orderState!.album.title;
        closeOrderModal();
        showPrintQRCode(result.data.payment_intent_id, finalAmount, result.data.code_url, albumId, albumTitle);
      } else {
        throw new Error('No payment intent');
      }
    }).catch(function(err: any) {
      showToast(err.message || '创建支付失败');
      btn.disabled = false;
      btn.style.opacity = '';
      btn.textContent = '确认付款';
    });
  });

  updatePhysTotal();
}

export function closeOrderModal(): void {
  document.getElementById('orderModalOverlay')!.classList.add('hidden');
  if (_orderOverlayHandler) {
    document.getElementById('orderModalOverlay')!.removeEventListener('click', _orderOverlayHandler);
    _orderOverlayHandler = null;
  }
  const modal = document.getElementById('orderModal')!;
  modal.innerHTML = '';
  modal.classList.remove('order-modal-wide');
  _orderState = null;
}

// =============================================================================
// Order Preview Pages
// =============================================================================

function _getPageCss(pageData: PageData): Promise<string> {
  const meta = pageData.templateId ? getTemplateMeta(pageData.templateId) : null;
  const cssPath = (meta && meta.css_path) || pageData.cssPath || '';
  const cssAlbumId: string = (meta && meta.album) || (_orderState && String(_orderState.albumId)) || cssPath;
  return loadTemplateCSS(cssAlbumId, cssPath);
}

function _renderPageIframe(pageData: PageData): Promise<string> {
  const spreadHalf = (pageData && pageData._spreadHalf) || null;
  const pageSize = getPageRenderSize(pageData);
  // For spread halves, pageSize is half-page size; double to get full render size
  const renderSize = spreadHalf ? { width: pageSize.width * 2, height: pageSize.height } : pageSize;

  return _getPageCss(pageData).then(function(cssText: string) {
    return loadTemplateRenderer().then(function(TR) {
      return TR.renderPage({
        templateId: pageData.templateId || '',
        albumId: _orderState ? _orderState.albumId || '' : '',
        html: pageData.html || '',
        htmlKind: 'saved-page',
        renderClass: pageData.renderClass || '',
        cssText: cssText || '',
        pageSize: renderSize,
        mode: 'marketplace',
        editable: false
      });
    });
  }).then(function(result) {
    let wrappedHtml = result.element.outerHTML;
    const scopedCSS = (result.styles && result.styles.scopedCSS) || '';
    const globalCSS = (result.styles && result.styles.globalCSS) || '';
    let pw = result.pageSize.width;
    let ph = result.pageSize.height;

    // Read CSP nonce from <meta> tag (injected by server) for iframe srcdoc scripts
    const nonceMeta = document.querySelector('meta[name="csp-nonce"]');
    const nonceAttr = nonceMeta ? ' nonce="' + nonceMeta.getAttribute('content') + '"' : '';

    if (spreadHalf) {
      const halfW = pageSize.width;
      const halfH = pageSize.height;
      const offsetX = spreadHalf === 'right' ? -halfW : 0;
      wrappedHtml = '<div style="width:' + halfW + 'px;height:' + halfH + 'px;overflow:hidden;position:relative;">' +
        '<div style="position:absolute;left:' + offsetX + 'px;width:' + pw + 'px;height:' + ph + 'px;">' + wrappedHtml + '</div></div>';
      pw = halfW;
      ph = halfH;
    }

    const srcDoc = '<!DOCTYPE html><html style="height:100%;"><head><meta charset="UTF-8"><style>' +
      globalCSS + '\n' + scopedCSS + '\n' +
      'html,body{margin:0;padding:0;height:100%;overflow:hidden;background:#f4f1ea;}\n' +
      '#stage{width:' + pw + 'px;height:' + ph + 'px;transform-origin:0 0;overflow:hidden;}\n' +
      '</style></head><body><div id="stage">' + wrappedHtml + '</div>' +
      '<script' + nonceAttr + '>function fit(){var s=Math.min(innerWidth/' + pw + ',innerHeight/' + ph + ');document.getElementById("stage").style.transform="scale("+s+")";}addEventListener("resize",fit);fit();<\/script></body></html>';
    return '<iframe srcdoc="' + srcDoc.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;') +
      '" style="width:100%;height:100%;border:none;" sandbox="allow-scripts allow-same-origin"></iframe>';
  });
}

function _isUnifiedSpread(spread: Spread): boolean {
  const left = spread.left, right = spread.right;
  if (!left || !right) return false;
  if (left.isUnifiedSpread || right.isUnifiedSpread) return true;
  const leftMeta = left.templateId ? getTemplateMeta(left.templateId) : null;
  const rightMeta = right.templateId ? getTemplateMeta(right.templateId) : null;
  return (leftMeta && leftMeta.category === '双页') ||
    (rightMeta && rightMeta.category === '双页') ||
    left.templateCategory === '双页' ||
    right.templateCategory === '双页';
}

function _makeSpreadHalfPage(pageData: PageData, half: 'left' | 'right'): PageData {
  // Like marketplace buildTemplatePageData: give half-page size + _spreadHalf
  const fullSize = getPageRenderSize(pageData);
  const halfW = Math.round(fullSize.width / 2);
  const halfH = fullSize.height;
  return {
    id: pageData.id,
    html: pageData.html,
    templateId: pageData.templateId,
    renderClass: pageData.renderClass,
    cssPath: pageData.cssPath,
    templateCategory: pageData.templateCategory,
    isUnifiedSpread: pageData.isUnifiedSpread,
    baseSize: halfW,
    pageSize: { width: halfW, height: halfH },
    _spreadHalf: half
  };
}

function _renderOrderPreviewPages(spread: Spread): Promise<string> {
  const isSpread = !!(spread.left && spread.right);
  if (!isSpread) {
    const pageData = spread.left || spread.right || spread as any;
    return _renderPageIframe(pageData).then(function(html: string) {
      return '<div class="order-preview-page single">' + html + '</div>';
    });
  }

  if (_isUnifiedSpread(spread)) {
    const pageData = spread.left!;
    const leftHalf = _makeSpreadHalfPage(pageData, 'left');
    const rightHalf = _makeSpreadHalfPage(pageData, 'right');
    return Promise.all([
      _renderPageIframe(leftHalf),
      _renderPageIframe(rightHalf)
    ]).then(function(results: string[]) {
      return '<div class="order-preview-page spread">' + results[0] + '</div>' +
        '<div class="order-preview-page spread">' + results[1] + '</div>';
    });
  }

  return Promise.all([
    _renderPageIframe(spread.left!),
    _renderPageIframe(spread.right!)
  ]).then(function(results: string[]) {
    return '<div class="order-preview-page spread">' + results[0] + '</div>' +
      '<div class="order-preview-page spread">' + results[1] + '</div>';
  });
}

function _buildOrderPreviewPages(albumDetail: AlbumDetail): OrderPreviewPage[] {
  const spreads = albumDetail.spreads || {};
  const pages: OrderPreviewPage[] = [];
  const spreadKeys = Object.keys(spreads).map(Number).sort(function(a: number, b: number) { return a - b; });

  spreadKeys.forEach(function(key: number) {
    const s = spreads[key];
    if (s._isCover) {
      pages.unshift({ label: '封面', type: 'cover', spread: s });
    } else if (s._isBackCover) {
      pages.push({ label: '封底', type: 'back', spread: s });
    } else {
      pages.push({ label: '内页', type: 'spread', spread: s });
    }
  });

  pages.sort(function(a: OrderPreviewPage, b: OrderPreviewPage) {
    if (a.type === 'cover') return -1;
    if (b.type === 'cover') return 1;
    if (a.type === 'back') return 1;
    if (b.type === 'back') return -1;
    return 0;
  });

  return pages;
}

// =============================================================================
// Physical Order (Print QR Code)
// =============================================================================

export function showPrintQRCode(intentId: string, amount: number, codeUrl: string, albumId: number, albumTitle: string): void {
  const overlay = document.getElementById('orderModalOverlay')!;
  const modal = document.getElementById('orderModal')!;
  overlay.classList.remove('hidden');
  modal.innerHTML =
    '<div class="qr-pay-modal">' +
      '<div class="qr-title">微信扫码支付</div>' +
      '<div class="qr-amount">¥' + amount.toFixed(2) + '</div>' +
      '<div class="qr-container"><div id="printPayQRCode"></div></div>' +
      '<div class="qr-hint">请使用微信扫描二维码完成支付</div>' +
      '<div class="qr-cancel" id="printQRCancelBtn">取消支付</div>' +
    '</div>';

  new QRCode(document.getElementById('printPayQRCode')!, {
    text: codeUrl,
    width: 200,
    height: 200,
    colorDark: '#000000',
    colorLight: '#ffffff'
  });

  const pollTimer = setInterval(function() {
    fetch('/api/orders/print/payment-intents/' + intentId, {
      credentials: 'include'
    }).then(function(r) { return r.json(); }).then(function(data: PaymentIntentStatus) {
      if (data.status === 'paid' && data.order_id) {
        clearInterval(pollTimer);
        // Mark album as completed so publish button appears
        fetch('/api/albums/' + albumId, {
          method: 'PUT',
          credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'completed', title: albumTitle })
        }).catch(function() {});
        invalidateAlbumsCache();
        overlay.classList.add('hidden');
        modal.innerHTML = '';
        showToast('支付成功');
        renderView('orders');
        document.querySelectorAll('.menu-item').forEach(function(m: Element) { m.classList.remove('active'); });
        const ordersBtn = document.querySelector('.menu-item[data-view="orders"]');
        if (ordersBtn) ordersBtn.classList.add('active');
        setCurrentView('orders');
      } else if (data.status === 'cancelled' || data.status === 'failed' || data.status === 'expired' || data.status === 'amount_mismatch') {
        clearInterval(pollTimer);
        overlay.classList.add('hidden');
        modal.innerHTML = '';
        showToast('支付未完成，请重新下单');
        renderView('works');
      }
    }).catch(function() {});
  }, 3000);

  (document.getElementById('printQRCancelBtn') as HTMLElement).addEventListener('click', function() {
    clearInterval(pollTimer);
    fetch('/api/orders/print/payment-intents/' + intentId, {
      method: 'DELETE',
      credentials: 'include'
    }).catch(function() {});
    overlay.classList.add('hidden');
    modal.innerHTML = '';
  });
}

// =============================================================================
// Order List Rendering
// =============================================================================

export async function renderMyOrders(container: HTMLElement): Promise<void> {
  trackEvent('orders_view');
  let orders: Order[] = [];
  try {
    const resp = await fetch('/api/orders', {
      credentials: 'include'
    });
    if (resp.ok) orders = await resp.json();
  } catch(e) { console.error('Failed to load orders:', e); }
  orders = orders.filter(function(order: Order) {
    return order && order.status !== 'pending';
  });
  let html = '<div class="section-head"><h2>历史订单</h2></div>';

  if (!orders.length) {
    html += '<div class="empty-state"><div class="empty-icon">&#x1F4CB;</div><p>暂无订单</p></div>';
  } else {
    html += '<div class="order-list">' +
      '<div class="order-item order-grid order-table-head">' +
      '<span></span>' +
      '<span class="order-cell">订单号</span>' +
      '<span class="order-cell">类型</span>' +
      '<span class="order-cell">标题</span>' +
      '<span class="order-cell">时间</span>' +
      '<span class="order-cell">金额</span>' +
      '<span class="order-cell">下载</span>' +
      '<span class="order-cell">快递</span>' +
      '<span class="order-cell">状态</span>' +
      '<span class="order-cell">操作</span>' +
      '</div>';
    orders.forEach(function(order: Order) {
      const statusLabel = STATUS_MAP[order.status] || order.status;
      const amount = parseFloat(order.amount) || 0;
      const albumTitle = order.album_title || ('相册 #' + order.album_id);
      const coverHtml = order.cover_url
        ? '<img src="' + order.cover_url + '" style="width:100%;height:100%;object-fit:cover;" alt="">'
        : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:var(--text-dim);font-size:20px;">&#x1F4F7;</div>';
      let statusDisplay = statusLabel;
      if (order.product_type === 'print' && order.status === 'production_file') {
        statusDisplay = order.can_cancel_within_window ? '' : statusLabel;
      }
      const deleteDisabled = order.status === 'making' || order.status === 'refund_processing';
      html += '<div class="order-item order-grid">' +
        '<div class="order-cover" style="width:48px;height:48px;border-radius:6px;overflow:hidden;background:var(--bg);">' + coverHtml + '</div>' +
        '<span class="order-cell order-id" style="font-size:12px;">#' + order.id + '</span>' +
        '<span class="order-cell" style="font-size:12px;font-weight:600;color:var(--text);">' + (TYPE_MAP[order.product_type] || order.product_type) + '</span>' +
        '<span class="order-cell order-title-cell" style="font-size:13px;font-weight:600;">《' + albumTitle + '》</span>' +
        '<span class="order-cell" style="font-size:12px;color:var(--text-dim);">' + formatTime(new Date(order.created_at).getTime()) + '</span>' +
        '<span class="order-cell" style="font-weight:700;color:var(--accent);">¥' + amount.toFixed(amount % 1 === 0 ? 0 : 1) + '</span>' +
        '<span class="order-cell">' + (order.status === 'paid' || order.status === 'completed' ? '<button class="btn btn-sm download-pdf-btn" data-id="' + order.album_id + '" data-order-id="' + order.id + '" data-published-template-id="' + (order.published_template_id || '') + '" data-title="' + albumTitle + '" style="color:var(--accent);font-weight:600;padding:4px 6px;">⬇</button>' : '<span style="color:var(--text-dim);font-size:12px;">—</span>') + '</span>' +
        '<span class="order-cell" style="font-size:12px;">' +
          (order.status === 'shipped' && order.tracking_company
            ? '<div>' + order.tracking_company + '</div><span class="tracking-copy" data-tracking="' + order.tracking_number + '" title="点击复制快递单号" style="font-size:11px;color:var(--text-dim);cursor:pointer;">' + order.tracking_number + '</span>'
            : '<span style="color:var(--text-dim);">—</span>') +
        '</span>' +
        '<span class="order-cell order-status-cell"><span class="status-tag status-' + order.status + (STATUS_TIPS[order.status] ? '" data-tip="' + STATUS_TIPS[order.status].replace(/"/g, '&quot;') + '"' : '') + '" style="font-size:12px;">' + statusDisplay + '</span>' +
        (order.product_type === 'print' && order.status === 'production_file' && order.can_cancel_within_window
          ? '<button class="btn btn-sm cancel-physical-btn" data-id="' + order.id + '" data-tip="15分钟内可取消订单" style="color:#e53e3e;border:1px solid #e53e3e;padding:2px 8px;font-size:12px;margin-top:4px;">取消订单</button>'
          : '') +

        '</span>' +
        '<span class="order-cell order-action-cell">' +
        '<button class="del-order-btn" data-id="' + order.id + '"' + (deleteDisabled ? ' disabled style="background:none;border:none;cursor:not-allowed;padding:2px;opacity:0.3;"' : ' style="background:none;border:none;cursor:pointer;padding:2px;"') + ' aria-label="删除"><svg style="height:17px" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M829.952 271.36h-126.976v-51.2a76.288 76.288 0 0 0-75.776-76.288h-256a76.288 76.288 0 0 0-75.776 76.8v51.2H168.448a25.6 25.6 0 0 0 0 51.2h661.504a25.6 25.6 0 0 0 0-51.712z m-178.176 0h-307.2v-51.2a26.112 26.112 0 0 1 25.6-25.6h256a24.576 24.576 0 0 1 24.576 25.6zM448.512 677.376V398.336a25.6 25.6 0 0 0-51.2 0v279.04a25.6 25.6 0 0 0 51.2 0zM601.088 677.376V398.336a25.6 25.6 0 0 0-51.2 0v279.04a25.6 25.6 0 0 0 51.2 0z" fill="#d81e06"/><path d="M735.744 346.624a25.6 25.6 0 0 0-25.6 25.6v381.44a102.4 102.4 0 0 1-102.4 102.4H390.656a102.4 102.4 0 0 1-102.4-102.4V372.224a25.6 25.6 0 0 0-51.2 0v381.44a153.6 153.6 0 0 0 153.6 153.6h217.6a153.6 153.6 0 0 0 153.6-153.6V372.224a25.6 25.6 0 0 0-26.112-25.6z" fill="#d81e06"/></svg></button>' +
        '</span>' +
        '</div>';
    });
    html += '</div>';
  }
  container.innerHTML = html;

  container.querySelectorAll('.del-order-btn').forEach(function(btn: Element) {
    btn.addEventListener('click', function(this: HTMLElement) {
      const id = this.getAttribute('data-id');
      showConfirm('删除订单', '确定删除订单 #' + id + ' 吗？').then(function(ok: boolean) {
        if (!ok) return;
        fetch('/api/orders/' + id, {
          method: 'DELETE',
          credentials: 'include'
        }).then(function() {
          renderView('orders');
        });
      });
    });
  });

  container.querySelectorAll('.download-pdf-btn').forEach(function(btn: Element) {
    btn.addEventListener('click', function(this: HTMLElement) {
      // downloadPDF is in a separate module or still inline
      const albumId = this.getAttribute('data-id');
      const albumTitle = this.getAttribute('data-title');
      const orderId = this.getAttribute('data-order-id');
      const publishedTemplateId = this.getAttribute('data-published-template-id');
      // Dispatch a custom event so the PDF download handler can pick it up
      document.dispatchEvent(new CustomEvent('dashboard:download-pdf', {
        detail: { albumId, albumTitle, btn: this, orderId, publishedTemplateId }
      }));
    });
  });

  container.querySelectorAll('.tracking-copy').forEach(function(el: HTMLElement) {
    el.addEventListener('click', function(e: MouseEvent) {
      e.stopPropagation();
      const tracking = (e.currentTarget as HTMLElement).getAttribute('data-tracking');
      navigator.clipboard.writeText(tracking!).then(function() {
        const orig = el.textContent;
        el.textContent = '已复制!';
        setTimeout(function() { el.textContent = orig; }, 1500);
      }).catch(function() {
        const input = document.createElement('input');
        input.value = tracking!;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        const orig = el.textContent;
        el.textContent = '已复制!';
        setTimeout(function() { el.textContent = orig; }, 1500);
      });
    });
  });

  container.querySelectorAll('.cancel-physical-btn').forEach(function(btn: Element) {
    btn.addEventListener('click', function(this: HTMLElement) {
      const id = this.getAttribute('data-id');
      showConfirm('取消订单', '确定取消订单 #' + id + '？取消后退款将在 1-2 个工作日内处理。').then(async function(ok: boolean) {
        if (!ok) return;
        try {
          const resp = await fetch('/api/orders/' + id + '/cancel-within-window', {
            method: 'POST',
            credentials: 'include'
          });
          if (resp.ok) {
            showToast('订单已取消，退款将在 1-2 个工作日内处理。');
            renderView('orders');
          } else {
            const err = await resp.json();
            showToast(err.message || '取消失败');
          }
        } catch(e) { showToast('取消失败'); }
      });
    });
  });

  container.querySelectorAll('.cs-qrcode-link').forEach(function(link: HTMLElement) {
    link.addEventListener('click', function(e: MouseEvent) {
      e.preventDefault();
      const orderId = (e.currentTarget as HTMLElement).getAttribute('data-order-id');
      showCustomerServiceQR(orderId!);
    });
  });
}

export function showCustomerServiceQR(orderId: string): void {
  const overlay = document.getElementById('orderModalOverlay')!;
  const modal = document.getElementById('orderModal')!;
  overlay.classList.remove('hidden');
  modal.innerHTML =
    '<div style="padding:24px;text-align:center;max-width:320px;">' +
      '<div style="font-size:16px;font-weight:700;margin-bottom:12px;">联系客服</div>' +
      '<img src="' + CUSTOMER_SERVICE_QR + '" style="width:180px;height:180px;border-radius:8px;border:1px solid var(--line);" alt="企业微信客服">' +
      '<div style="font-size:12px;color:var(--text-dim);margin-top:12px;line-height:1.6;">添加企业微信客服，发送订单号，我们会尽快处理。<br>客服通常会在 24 小时内响应；法定节假日或咨询高峰期可能顺延。</div>' +
      '<div style="margin-top:12px;"><button class="btn btn-sm" id="csCopyOrderId" data-id="' + orderId + '">复制订单号 #' + orderId + '</button></div>' +
      '<div style="margin-top:16px;"><button class="btn" id="csCloseBtn">关闭</button></div>' +
    '</div>';
  (document.getElementById('csCloseBtn') as HTMLElement).addEventListener('click', function() {
    document.getElementById('orderModalOverlay')!.classList.add('hidden');
    document.getElementById('orderModal')!.innerHTML = '';
    document.getElementById('orderModal')!.classList.remove('order-modal-wide');
  });
  (document.getElementById('csCopyOrderId') as HTMLElement).addEventListener('click', function(this: HTMLElement) {
    const id = this.getAttribute('data-id')!;
    navigator.clipboard.writeText('#' + id).then(function() {
      const orig = (document.getElementById('csCopyOrderId') as HTMLElement).textContent;
      (document.getElementById('csCopyOrderId') as HTMLElement).textContent = '已复制!';
      setTimeout(function() { (document.getElementById('csCopyOrderId') as HTMLElement).textContent = orig; }, 1500);
    });
  });
}
